import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
    Avatar,
    Button,
    Card,
    Empty,
    Image,
    Input,
    Select,
    Table,
    Tag,
    Typography,
    Upload,
} from "antd";
import type { TableProps, UploadProps } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { isAxiosError } from "axios";
import { Link as RouterLink } from "react-router";

import { CustomResult, useAppMessage } from "@repo/ui";
import { exportMerchantProductsApi, importMerchantProductsCostPricesApi } from "@src/api";
import { useMerchantProducts, useMerchants } from "@src/queries";
import type { ErrorResponseType, MerchantProductType } from "@src/types";
import { buildKaspiProductLink, buildProductImageUrl, formatPrice } from "./utils";

type TableChangeArgs = Parameters<NonNullable<TableProps<MerchantProductType>["onChange"]>>;

const DEFAULT_PAGE_SIZE = 8;
type AvailabilityFilterValue = "all" | "available" | "unavailable";

const AVAILABILITY_OPTIONS: { label: string; value: AvailabilityFilterValue }[] = [
    { label: "Все товары", value: "all" },
    { label: "В наличии", value: "available" },
    { label: "Нет в наличии", value: "unavailable" },
];

const { Title, Text, Link } = Typography;
const { PreviewGroup } = Image;

export function MerchantProductsCard() {
    const merchantsQuery = useMerchants();
    const merchants = merchantsQuery.data?.data ?? [];

    const message = useAppMessage();
    const [selectedMerchantId, setSelectedMerchantId] = useState<string>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [availabilityFilter, setAvailabilityFilter] =
        useState<AvailabilityFilterValue>("available");
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        if (!merchants.length) {
            setSelectedMerchantId(undefined);
            return;
        }

        setSelectedMerchantId((prev) => {
            if (prev && merchants.some((merchant) => merchant.id === prev)) {
                return prev;
            }
            return merchants[0]?.id;
        });
    }, [merchants]);

    useEffect(() => {
        setPage(1);
    }, [selectedMerchantId, availabilityFilter, searchTerm]);

    const availabilityValue = useMemo(() => {
        if (availabilityFilter === "available") return true;
        if (availabilityFilter === "unavailable") return false;
        return undefined;
    }, [availabilityFilter]);

    const productsQuery = useMerchantProducts({
        merchantId: selectedMerchantId,
        page,
        size: pageSize,
        search: searchTerm,
        available: availabilityValue,
    });
    const {
        data: productsData,
        isFetching,
        isLoading,
        isError,
        error,
        refetch: refetchProducts,
    } = productsQuery;

    const handleMerchantChange = useCallback((value: string) => {
        setSelectedMerchantId(value);
    }, []);

    const handlePaginationChange = useCallback((...args: TableChangeArgs) => {
        const [pagination] = args;
        if (pagination?.current) {
            setPage(pagination.current);
        }
        if (pagination?.pageSize) {
            setPageSize(pagination.pageSize);
        }
    }, []);

    const handleSearchSubmit = useCallback((value: string) => {
        const trimmed = value.trim();
        setSearchInput(value);
        setSearchTerm(trimmed ? trimmed : null);
    }, []);

    const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchInput(value);
        if (!value) {
            setSearchTerm(null);
        }
    }, []);

    const handleExportProducts = useCallback(async () => {
        if (!selectedMerchantId) {
            return;
        }

        try {
            setIsExporting(true);
            const { blob, filename, contentType } = await exportMerchantProductsApi(
                selectedMerchantId,
                {
                    page,
                    size: pageSize,
                    search: searchTerm ?? undefined,
                    available: availabilityValue,
                },
            );

            const fileBlob = blob.type ? blob : new Blob([blob], { type: contentType });
            const objectUrl = URL.createObjectURL(fileBlob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
            message.success("Файл с товарами сформирован");
        } catch (error) {
            const fallbackMessage = "Не удалось экспортировать товары";
            if (isAxiosError<ErrorResponseType>(error)) {
                message.error(error.response?.data?.detail ?? fallbackMessage);
                return;
            }
            message.error(fallbackMessage);
        } finally {
            setIsExporting(false);
        }
    }, [availabilityValue, message, page, pageSize, searchTerm, selectedMerchantId]);

    const handleImportProducts = useCallback(
        async (file: File) => {
            if (!selectedMerchantId) {
                return;
            }

            try {
                setIsImporting(true);
                const result = await importMerchantProductsCostPricesApi(selectedMerchantId, file);
                message.success(
                    `Импорт завершен: всего ${result.total_rows}, добавлено ${result.created}, 0 себестоимость — ${result.skipped_zero_cost}, ошибки — ${result.skipped_invalid_rows}, другие магазины — ${result.skipped_foreign_products}`,
                );
                await refetchProducts();
            } catch (error) {
                const fallbackMessage = "Не удалось импортировать товары";
                if (isAxiosError<ErrorResponseType>(error)) {
                    message.error(error.response?.data?.detail ?? fallbackMessage);
                    return;
                }
                message.error(fallbackMessage);
            } finally {
                setIsImporting(false);
            }
        },
        [message, refetchProducts, selectedMerchantId],
    );

    const uploadProps = useMemo<UploadProps>(() => {
        return {
            accept: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            showUploadList: false,
            disabled: !selectedMerchantId || isImporting,
            beforeUpload: async (file) => {
                await handleImportProducts(file as File);
                return Upload.LIST_IGNORE;
            },
        };
    }, [handleImportProducts, isImporting, selectedMerchantId]);

    const merchantOptions = useMemo(
        () =>
            merchants.map((merchant) => ({
                label: merchant.store_name || merchant.merchant_uid,
                value: merchant.id,
            })),
        [merchants],
    );

    const products = productsData?.data ?? [];
    const total = productsData?.total ?? 0;

    const columns = useMemo<TableProps<MerchantProductType>["columns"]>(
        () => [
            {
                title: "Товар",
                dataIndex: "title",
                key: "title",
                render: (_value, record) => {
                    const title = record.title || record.product?.title || "Без названия";
                    const link = buildKaspiProductLink(record.product?.shop_link);
                    const imageUrls = (record.product?.images ?? [])
                        .map(buildProductImageUrl)
                        .filter((url): url is string => Boolean(url));
                    const imageSrc = imageUrls[0];

                    return (
                        <div className="flex items-center gap-3">
                            {imageSrc ? (
                                <PreviewGroup items={imageUrls}>
                                    <Image
                                        src={imageSrc}
                                        width={56}
                                        height={56}
                                        className="rounded-lg object-cover"
                                        style={{ objectFit: "cover" }}
                                        preview={{ mask: <span className="text-xs">Открыть</span> }}
                                    />
                                </PreviewGroup>
                            ) : (
                                <Avatar shape="square" size={56} className="bg-gray-100 text-base">
                                    {title?.charAt(0)?.toUpperCase() ?? "?"}
                                </Avatar>
                            )}
                            <div className="min-w-0 flex-1">
                                <RouterLink
                                    to={`/products/${record.id}`}
                                    className="block truncate text-sm font-semibold text-[#1677ff]"
                                >
                                    {title}
                                </RouterLink>
                                {link && (
                                    <Link
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-xs text-gray-500"
                                    >
                                        Открыть в Kaspi
                                    </Link>
                                )}
                                <Text type="secondary" className="block text-xs text-gray-500">
                                    SKU: {record.sku}
                                </Text>
                                {record.brand && (
                                    <Text type="secondary" className="block text-xs text-gray-500">
                                        Бренд: {record.brand}
                                    </Text>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "Себестоимость",
                dataIndex: "current_cost_price",
                key: "current_cost_price",
                width: 160,
                render: (value?: number | null) => formatPrice(value),
            },
            {
                title: "Цена",
                key: "price-range",
                width: 200,
                render: (_value, record) => {
                    const minLabel = formatPrice(record.min_price);
                    const maxLabel = formatPrice(record.max_price);

                    if (minLabel === maxLabel) {
                        return minLabel;
                    }

                    if (minLabel === "—") {
                        return maxLabel;
                    }

                    if (maxLabel === "—") {
                        return minLabel;
                    }

                    return `${minLabel} – ${maxLabel}`;
                },
            },
            {
                title: "Наличие",
                dataIndex: "available",
                key: "available",
                width: 140,
                render: (value: boolean) => (
                    <Tag color={value ? "green" : "default"}>{value ? "В наличии" : "Нет"}</Tag>
                ),
            },
            {
                title: "Бренд",
                dataIndex: "brand",
                key: "brand",
                width: 160,
            },
        ],
        [],
    );

    if (merchantsQuery.isError) {
        return (
            <CustomResult
                type="error"
                title="Не удалось загрузить магазины"
                axiosError={merchantsQuery.error}
                withCard
            />
        );
    }

    const showEmptyState = !merchantsQuery.isLoading && merchants.length === 0;

    return (
        <Card
            title={
                <div className="flex flex-col">
                    <Title level={4} className="!m-0">
                        Товары магазина
                    </Title>
                    <Text type="secondary" className="text-sm text-gray-500">
                        Выберите магазин и следите за остатками
                    </Text>
                </div>
            }
            className="w-full"
        >
            {showEmptyState ? (
                <Empty description="Добавьте хотя бы один магазин, чтобы увидеть товары" />
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-3">
                        <div className="flex min-w-[240px] flex-1 flex-wrap items-center gap-3">
                            <Input.Search
                                placeholder="Поиск по SKU или названию"
                                allowClear
                                value={searchInput}
                                onChange={handleSearchChange}
                                onSearch={handleSearchSubmit}
                                className="w-full md:max-w-xs"
                            />
                            <Select
                                placeholder="Выберите магазин"
                                options={merchantOptions}
                                value={selectedMerchantId}
                                onChange={handleMerchantChange}
                                loading={merchantsQuery.isFetching && !merchants.length}
                                className="min-w-[240px]"
                            />

                            <Select<AvailabilityFilterValue>
                                value={availabilityFilter}
                                options={AVAILABILITY_OPTIONS}
                                onChange={(value) => setAvailabilityFilter(value)}
                                className="min-w-[180px]"
                            />
                        </div>

                        <div className="ml-auto flex flex-wrap gap-2">
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                loading={isExporting}
                                disabled={!selectedMerchantId || isImporting}
                                onClick={handleExportProducts}
                            >
                                Экспорт .xlsx
                            </Button>

                            <Upload {...uploadProps}>
                                <Button
                                    icon={<UploadOutlined />}
                                    loading={isImporting}
                                    disabled={!selectedMerchantId || isImporting}
                                >
                                    Импорт .xlsx
                                </Button>
                            </Upload>
                        </div>
                    </div>

                    {isError ? (
                        <CustomResult
                            type="error"
                            title="Не удалось загрузить товары"
                            axiosError={error}
                        />
                    ) : (
                        <Table<MerchantProductType>
                            rowKey={(record) => record.id}
                            columns={columns}
                            dataSource={products}
                            loading={isLoading || isFetching}
                            pagination={{
                                current: page,
                                pageSize,
                                total,
                                showSizeChanger: true,
                                pageSizeOptions: [10, 20, 50],
                            }}
                            scroll={{ x: 900 }}
                            onChange={handlePaginationChange}
                            locale={{
                                emptyText: <Empty description="Продукты не найдены" />,
                            }}
                        />
                    )}
                </div>
            )}
        </Card>
    );
}
